import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { unwrap } from '@/api/envelope';
import {
  readProfile,
  removeCoverImage,
  removeLogo,
  updateProfile,
  uploadCoverImage,
  uploadLogo,
  type OrganizationProfileResponse as OrganizationProfile,
} from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { ImageUpload } from '@/components/form/ImageUpload';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardSkeleton, SectionSkeleton } from '@/components/ui/Skeletons';
import { useToast } from '@/components/ui/useToast';
import { useReadOnly } from '@/auth/useViewingAs';
import { COVER_MAX_BYTES, LOGO_MAX_BYTES } from '@/lib/images';
import { STATUS_COLOUR, STATUS_LABELS, isWritable } from '@/lib/organization';

import { organizationProfileKey, profileKey } from './keys';
import { ProfileForm, type ProfileFormHandle } from './ProfileForm';
import {
  toProfileFormValues,
  toProfileUpdatePayload,
  type ProfileDirtyFields,
  type ProfileFormOutput,
} from './profileSchema';

/** Under the title, while loading and once loaded alike. */
const DESCRIPTION = 'What supporters see about your organization in the app.';

/** Which of the two images a call is about. */
type ImageKind = 'logo' | 'cover-image';

/** What a save has been asked to send. */
interface PendingSave {
  values: ProfileFormOutput;
  dirtyFields: ProfileDirtyFields;
}

/**
 * The status and the plan, and nothing else commercial.
 *
 * Deliberately thin. A Super Admin needs to know whether their account is live
 * and what plan they are on; the cost, the contract dates and the invoices are
 * the Billing page's, and a field that is not fetched cannot be shown by
 * accident.
 */
function StandingCard({ record }: { record: OrganizationProfile }) {
  return (
    <Paper
      variant="outlined"
      data-testid="profile-standing-card"
      sx={{ p: 2, mb: 3, maxWidth: 760 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* The word carries the status; the colour only reinforces it. */}
          <Chip
            label={STATUS_LABELS[record.status] ?? record.status}
            color={STATUS_COLOUR[record.status] ?? 'default'}
            size="small"
            data-testid="profile-status-chip"
          />
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="profile-plan-value"
          >
            {record.plan_name ? `${record.plan_name} plan` : 'No plan set'}
          </Typography>
        </Box>

        <Button
          component={RouterLink}
          to={ROUTES.account.billing}
          variant="ghost"
          size="sm"
          data-testid="profile-billing-link"
        >
          View billing
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * The organization's profile, as its own Super Admin sees and edits it.
 *
 * The page owns three things the form does not: the query, the two image
 * uploads - which save on their own, without the form - and the decision that
 * the whole thing is read-only because the account is suspended.
 */
export function ProfilePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  // Staff viewing the organization may read this page and change nothing.
  const viewing = useReadOnly();

  const form = useRef<ProfileFormHandle>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ImageKind | null>(null);

  /**
   * Keep a record the server sent back, here and in the top bar's copy, so a
   * new or removed logo shows in the bar at once. The bar keys its copy by the
   * organization's id, which is the record's own.
   */
  function keep(record: OrganizationProfile) {
    queryClient.setQueryData(profileKey, record);
    queryClient.setQueryData(organizationProfileKey(record.id), record);
  }

  const query = useQuery({
    queryKey: profileKey,
    queryFn: async () => {
      const reply = await readProfile({ throwOnError: true });
      return unwrap(reply.data);
    },
  });

  /*
   * Bring a failure into view. The form is long, and a message at the top of it
   * is out of sight from the Save button. Focus goes with the scroll, so it is
   * announced rather than only seen.
   */
  useEffect(() => {
    if (!formError) return;
    alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alertRef.current?.focus();
  }, [formError]);

  const save = useMutation({
    mutationFn: async ({ values, dirtyFields }: PendingSave) => {
      const reply = await updateProfile({
        // The record the form was filled from, whose contact addresses the
        // form does not show but must not lose.
        body: toProfileUpdatePayload(values, dirtyFields, {
          primary_contact: query.data?.primary_contact,
          public_contact: query.data?.public_contact,
        }),
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => {
      setFormError(null);
    },
    onSuccess: async (record) => {
      // What the server stored, not what was typed. They are usually the same;
      // when they are not, the server is right.
      form.current?.reset(toProfileFormValues(record));
      keep(record);
      toast.show({ message: 'Profile saved.' });
      await queryClient.invalidateQueries({ queryKey: profileKey });
    },
    onError: (error) => {
      // Each field's message lands on its field; whatever concerns no field
      // comes back to sit in the alert above the form. The name clash lands
      // on Name inside the form's own mapping.
      const { remainder } = form.current?.applyServerErrors(error) ?? {
        remainder: 'Your profile could not be saved. Please try again.',
      };
      if (remainder) setFormError(remainder);
    },
  });

  const removeImage = useMutation({
    mutationFn: async (kind: ImageKind) => {
      const remove = kind === 'logo' ? removeLogo : removeCoverImage;
      const reply = await remove({ throwOnError: true });
      return unwrap(reply.data);
    },
    onSuccess: (record, kind) => {
      // The reply is the whole profile, not just the field that changed, so
      // there is nothing to refetch.
      keep(record);
      toast.show({
        message: kind === 'logo' ? 'Logo removed.' : 'Cover image removed.',
      });
    },
    onError: () => {
      setFormError('That image could not be removed. Please try again.');
    },
    // Closed when the request is answered, not when Remove is pressed, so
    // the dialog's own pending state is what the person sees and a second
    // press cannot send the request twice.
    onSettled: () => setRemoving(null),
  });

  if (query.isPending) {
    return (
      <Box data-testid="profile-page">
        <PageHeader title="Profile" description={DESCRIPTION} />
        {/* Shapes, not a form: a form full of empty boxes reads as an
            organization with nothing in it. The same blocks the page will have
            - the standing card and the form's six sections - each as tall as
            it is once loaded, on a phone and on a desktop, so the page does
            not jump when the profile arrives. */}
        <Box sx={{ maxWidth: 760 }}>
          <Box sx={{ mb: 3 }}>
            <CardSkeleton
              lines={0}
              label="Loading your organization..."
              minHeight={{ xs: 105, md: 65 }}
            />
          </Box>
          <Stack spacing={3}>
            <SectionSkeleton fields={3} minHeight={{ xs: 397, md: 377 }} />
            {/* The two images sit side by side from tablet width up. */}
            <SectionSkeleton fields={2} minHeight={{ xs: 545, sm: 305 }} />
            <SectionSkeleton fields={2} minHeight={{ xs: 315, md: 295 }} />
            <SectionSkeleton fields={1} minHeight={160} />
            <SectionSkeleton fields={3} minHeight={{ xs: 360, md: 340 }} />
            <SectionSkeleton fields={3} minHeight={{ xs: 360, md: 340 }} />
            <Skeleton variant="rounded" width={130} height={37} />
          </Stack>
        </Box>
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Box data-testid="profile-page" sx={{ maxWidth: 560 }}>
        <PageHeader title="Profile" />
        <ErrorState
          title="Your organization's profile could not be loaded."
          onRetry={() => void query.refetch()}
          testId="profile"
        />
      </Box>
    );
  }

  const record = query.data;
  // Read-only for either reason: the organization's status, or staff viewing
  // it. Only the first has a notice - the viewing banner already says the
  // second on every page.
  const statusForbids = !isWritable(record.status);
  const readOnly = statusForbids || viewing;

  /**
   * Send one image and redraw from what came back.
   *
   * The upload endpoint answers with the whole profile rather than the address
   * alone, so the page takes the record from the reply. Refetching afterwards
   * would be a second round trip to learn what it already has.
   */
  async function upload(kind: ImageKind, file: File) {
    const send = kind === 'logo' ? uploadLogo : uploadCoverImage;
    const reply = await send({ body: { file }, throwOnError: true });
    keep(unwrap(reply.data));
    toast.show({ message: kind === 'logo' ? 'Logo updated.' : 'Cover image updated.' });
  }

  return (
    <Box data-testid="profile-page">
      <PageHeader title="Profile" description={DESCRIPTION} />

      <StandingCard record={record} />

      {statusForbids && (
        <Alert
          severity="warning"
          role="alert"
          data-testid="profile-read-only-alert"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          This organization is{' '}
          {(STATUS_LABELS[record.status] ?? record.status).toLowerCase()}, so its profile
          cannot be changed. Contact Activist Technologies to discuss it.
        </Alert>
      )}

      {formError && (
        <Alert
          severity="error"
          role="alert"
          ref={alertRef}
          tabIndex={-1}
          data-testid="profile-save-error"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          {formError}
        </Alert>
      )}

      <ProfileForm
        ref={form}
        /* Keyed on the record, so a profile that arrives or changes underneath
           rebuilds the form rather than leaving it holding the old values. */
        key={`${record.id}:${record.status}`}
        defaultValues={toProfileFormValues(record)}
        onSubmit={(values, dirtyFields) => save.mutate({ values, dirtyFields })}
        pending={save.isPending}
        readOnly={readOnly}
        images={
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <ImageUpload
              label="Organization logo"
              alt="Organization logo"
              src={record.logo?.url}
              maxBytes={LOGO_MAX_BYTES}
              disabled={readOnly || removeImage.isPending}
              onUpload={(file) => upload('logo', file)}
              onRemove={() => setRemoving('logo')}
              testId="profile-logo"
            />
            <ImageUpload
              label="Cover image"
              alt="Cover image"
              src={record.cover_image?.url}
              maxBytes={COVER_MAX_BYTES}
              disabled={readOnly || removeImage.isPending}
              onUpload={(file) => upload('cover-image', file)}
              onRemove={() => setRemoving('cover-image')}
              testId="profile-cover"
            />
          </Box>
        }
      />

      {/* Asked rather than done: a removed image cannot be put back from here,
          and the file itself is gone from the app. */}
      <ConfirmDialog
        open={removing !== null}
        title={removing === 'logo' ? 'Remove the logo?' : 'Remove the cover image?'}
        description="Supporters will see the organization without it until another one is uploaded."
        confirmLabel="Remove"
        destructive
        testId="profile-remove-image-dialog"
        pending={removeImage.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing && !removeImage.isPending) removeImage.mutate(removing);
        }}
      />
    </Box>
  );
}
